import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChevronRight, Minus, Plus, ShoppingBag, Check, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isCustomOrder, setIsCustomOrder] = useState(false);
  const [customNotes, setCustomNotes] = useState('');
  const [customPreferences, setCustomPreferences] = useState('');
  const [customImage, setCustomImage] = useState(null);
  const [customImagePreview, setCustomImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/slug/${slug}`);
        setProduct(res.data);
        
        if (res.data.category_id) {
          const catRes = await axios.get(`${API}/categories/${res.data.category_id}`);
          setCategory(catRes.data);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, navigate]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = () => setCustomImagePreview(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload`, formData);
      setCustomImage(res.data.url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
      setCustomImagePreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    const customOptions = isCustomOrder ? {
      isCustom: true,
      notes: customNotes,
      preferences: customPreferences,
      image: customImage
    } : null;

    addToCart(product, quantity, customOptions);
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className="min-h-screen section-spacing">
        <div className="container-handcraft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square rounded-2xl skeleton" />
            <div className="space-y-6">
              <div className="h-8 w-3/4 skeleton rounded" />
              <div className="h-6 w-1/4 skeleton rounded" />
              <div className="h-24 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const price = product.sale_price || product.price;
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const inStock = product.stock > 0;

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-sand">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/shop" className="hover:text-charcoal transition-colors">Shop</Link>
            {category && (
              <>
                <ChevronRight className="w-4 h-4" />
                <Link to={`/shop/${category.slug}`} className="hover:text-charcoal transition-colors">
                  {category.name}
                </Link>
              </>
            )}
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Content */}
      <section className="section-spacing">
        <div className="container-handcraft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Images */}
            <div className="space-y-4">
              <motion.div 
                className="aspect-square rounded-2xl overflow-hidden bg-sand"
                layoutId={`product-image-${product.id}`}
              >
                <img
                  src={product.images?.[selectedImage] || 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              {product.images?.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                        selectedImage === index ? 'border-accent' : 'border-transparent'
                      }`}
                    >
                      <img src={image} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              {/* Category */}
              {category && (
                <Link 
                  to={`/shop/${category.slug}`}
                  className="text-xs uppercase tracking-widest text-accent font-medium hover:text-accent/80 transition-colors"
                >
                  {category.name}
                </Link>
              )}

              {/* Title */}
              <h1 className="font-serif text-3xl md:text-4xl text-charcoal">
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-center gap-3">
                <span className={`text-2xl font-semibold ${hasDiscount ? 'text-terracotta' : 'text-charcoal'}`}>
                  ₹{price.toLocaleString()}
                </span>
                {hasDiscount && (
                  <span className="text-lg text-muted-foreground line-through">
                    ₹{product.price.toLocaleString()}
                  </span>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                {inStock ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-sage" />
                    <span className="text-sm text-sage font-medium">In Stock ({product.stock} available)</span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-destructive" />
                    <span className="text-sm text-destructive font-medium">Out of Stock</span>
                  </>
                )}
              </div>

              {/* Description */}
              <p className="text-muted-foreground leading-relaxed">
                {product.description}
              </p>

              {/* Custom Order Option */}
              {product.is_custom_available && (
                <div className="border border-sand rounded-xl p-6 space-y-4 bg-muted/30">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="custom-order"
                      checked={isCustomOrder}
                      onCheckedChange={setIsCustomOrder}
                      data-testid="custom-order-checkbox"
                    />
                    <div>
                      <Label htmlFor="custom-order" className="font-medium cursor-pointer">
                        I need a custom version of this product
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Add your personalization details below
                      </p>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isCustomOrder && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4 pt-4 border-t border-sand"
                      >
                        <div>
                          <Label className="label-handcraft">Custom Notes</Label>
                          <Textarea
                            placeholder="Describe your requirements..."
                            value={customNotes}
                            onChange={(e) => setCustomNotes(e.target.value)}
                            className="min-h-[100px]"
                            data-testid="custom-notes"
                          />
                        </div>
                        <div>
                          <Label className="label-handcraft">Preferences (colors, size, etc.)</Label>
                          <Input
                            placeholder="e.g., Blue color, 8x10 inches"
                            value={customPreferences}
                            onChange={(e) => setCustomPreferences(e.target.value)}
                            data-testid="custom-preferences"
                          />
                        </div>
                        <div>
                          <Label className="label-handcraft">Reference Image (Optional)</Label>
                          <div className="mt-2">
                            {customImagePreview ? (
                              <div className="relative w-32 h-32 rounded-lg overflow-hidden">
                                <img src={customImagePreview} alt="Preview" className="w-full h-full object-cover" />
                                <button
                                  onClick={() => {
                                    setCustomImage(null);
                                    setCustomImagePreview('');
                                  }}
                                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-charcoal/80 text-white flex items-center justify-center"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <label className="upload-area flex flex-col items-center gap-2 cursor-pointer">
                                <Upload className="w-8 h-8 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                  {uploading ? 'Uploading...' : 'Click to upload image'}
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                  className="hidden"
                                  data-testid="custom-image-upload"
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <div className="quantity-input">
                  <button 
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    data-testid="quantity-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input 
                    type="number" 
                    value={quantity} 
                    readOnly 
                    data-testid="quantity-value"
                  />
                  <button 
                    onClick={() => setQuantity(q => q + 1)}
                    disabled={quantity >= product.stock}
                    data-testid="quantity-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                <Button
                  onClick={handleAddToCart}
                  disabled={!inStock}
                  className="btn-handcraft bg-charcoal text-cream hover:bg-charcoal/90 flex-1"
                  data-testid="add-to-cart"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  {inStock ? 'Add to Cart' : 'Out of Stock'}
                </Button>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 pt-4">
                {product.is_custom_available && (
                  <span className="badge-handcraft bg-sage/20 text-sage">
                    <Check className="w-3 h-3 mr-1" />
                    Custom Available
                  </span>
                )}
                <span className="badge-handcraft bg-accent/20 text-accent">
                  <Check className="w-3 h-3 mr-1" />
                  Handcrafted
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
